import type { Lens } from "@/hooks/use-lens";
import type { WasteLineStats } from "@/hooks/use-line-stats";

/** One row of the What's-working widget (POS2-875). */
export type Win = {
  lineKey: string;
  label: string;
  /** streak = value down every period of the badge window; hold = never rose
   * above the window baseline and ended lower; single = the fallback "best
   * available trend" (largest one-period improvement). */
  kind: "streak" | "hold" | "single";
  /** Periods the badge reports: streak length, the full window for holds, 1 for the fallback. */
  periods: number;
  /** % down vs the win's baseline, in (0, 100]. */
  pctDown: number;
  /** Lens units kept per week: (baseline − current), weekly-normalised. */
  keptPerWeek: number;
  /** Current-period image-backed captures exist — the row can link into The Proof. */
  hasEvidence: boolean;
};

export type Wins = { wins: Win[]; totalKeptPerWeek: number };

const MAX_ROWS = 3;

function metric(line: WasteLineStats, i: number, lens: Lens): number {
  return lens === "weight" ? line.periods[i].total_weight : line.periods[i].total_cost;
}

function hasCurrentCapture(line: WasteLineStats): boolean {
  return line.periods[line.periods.length - 1].tagged_capture_count > 0;
}

/**
 * Any events in the periods before the current one. When false, an empty wins
 * panel means "no comparable history in this window" (site data starts inside
 * the range), not "nothing improved" — the empty copy should say so.
 */
export function hasPriorHistory(lines: WasteLineStats[]): boolean {
  return lines.some((line) => line.periods.slice(0, -1).some((p) => p.event_count > 0));
}

/**
 * The wins to show (design §9): lines with a sustained decline (lens value down
 * 3+ consecutive periods ending now) or held under the window baseline
 * (`periods[0]` — no per-line threshold config exists, so the threshold is the
 * window's own starting level) while ending lower. Kept = baseline − current,
 * normalised to a weekly rate. Sorted by kept desc, capped at 3; when any
 * qualifying line has a current-period capture, at least one shown row carries
 * the evidence link. When nothing qualifies, falls back to the largest
 * single-period improvement; null only when no line improved at all.
 */
export function selectWins(
  lines: WasteLineStats[],
  lens: Lens,
  periodLengthDays: number,
): Wins | null {
  const weekly = 7 / Math.max(1, periodLengthDays);
  // `remainder` is the endpoint's everything-else aggregate, not a line.
  // `unidentified` is the L1 catch-all (no recipe/item/subcategory/category); a fall in
  // it is ambiguous between a real drop and reclassification into named lines, so it can't
  // be presented as "£/wk kept" (POS2-924, Option A). Excluding it here drops it from both
  // the qualifiers and the `bestSingleImprovement` fallback, which share this list.
  const eligible = lines.filter(
    (l) => l.line_key !== "remainder" && l.line_key !== "unidentified" && l.periods.length >= 2,
  );

  const qualifiers: Win[] = [];
  for (const line of eligible) {
    const vals = line.periods.map((_, i) => metric(line, i, lens));
    const baseline = vals[0];
    const kept = baseline - vals[vals.length - 1];
    if (kept <= 0) continue;

    let streak = 0;
    for (let i = vals.length - 1; i > 0 && vals[i] < vals[i - 1]; i--) streak++;
    const held = vals.every((v) => v <= baseline);
    if (streak < 3 && !held) continue;

    qualifiers.push({
      lineKey: line.line_key,
      label: line.label,
      kind: streak >= 3 ? "streak" : "hold",
      periods: streak >= 3 ? streak : vals.length,
      pctDown: (kept / baseline) * 100,
      keptPerWeek: kept * weekly,
      hasEvidence: hasCurrentCapture(line),
    });
  }

  qualifiers.sort((a, b) => b.keptPerWeek - a.keptPerWeek);
  const wins = qualifiers.slice(0, MAX_ROWS);
  // At least one shown win must carry the evidence link when any qualifier has
  // a capture (ticket) — swap the lowest row for the best evidenced one.
  if (wins.length > 0 && !wins.some((w) => w.hasEvidence)) {
    const evidenced = qualifiers.find((w) => w.hasEvidence);
    if (evidenced) wins[wins.length - 1] = evidenced;
  }
  if (wins.length === 0) {
    const fallback = bestSingleImprovement(eligible, lens, weekly);
    if (!fallback) return null;
    wins.push(fallback);
  }

  return { wins, totalKeptPerWeek: wins.reduce((sum, w) => sum + w.keptPerWeek, 0) };
}

/** The best available trend: the largest positive last-period improvement. */
function bestSingleImprovement(lines: WasteLineStats[], lens: Lens, weekly: number): Win | null {
  let best: { line: WasteLineStats; delta: number; prev: number } | null = null;
  for (const line of lines) {
    const prev = metric(line, line.periods.length - 2, lens);
    const delta = prev - metric(line, line.periods.length - 1, lens);
    if (delta <= 0) continue;
    if (!best || delta > best.delta) best = { line, delta, prev };
  }
  if (!best) return null;

  return {
    lineKey: best.line.line_key,
    label: best.line.label,
    kind: "single",
    periods: 1,
    pctDown: (best.delta / best.prev) * 100,
    keptPerWeek: best.delta * weekly,
    hasEvidence: hasCurrentCapture(best.line),
  };
}
